import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { Copy, User, Home, Users, Info } from 'lucide-react';
import { readableDate } from './createShiftServiceRules';
import { addDays, duplicatePayload, duplicateErrorMessage } from './shiftDuplicateRules';

/** BS3-002: copy a 1:1 or SIL house shift to one other date — same rules as Create Shift; staff are never copied. */
export function DuplicateShiftDialog({ shift, open, onClose, onSaved, segments = [] }) {
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && shift) { setForm({ date: addDays(shift.date, 1), start_time: shift.start_time || '', end_time: shift.end_time || '' }); setError(''); }
  }, [open, shift]);

  if (!shift) return null;
  const sil = !!(shift.house_id || shift.linked_house_id) || String(shift.support_type || '').toLowerCase() === 'sil';
  const items = (segments || []).filter((r) => r.status !== 'DELETED');
  const residents = (shift.linked_participant_details || []).map((p) => p.name).filter(Boolean);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(''); };

  const save = async () => {
    const { body, error: problem } = duplicatePayload(shift, form);
    if (problem) { setError(problem); return; }
    setSaving(true);
    try {
      const res = await api.post(`/roster/shifts/${shift.id}/duplicate`, body);
      toast.success(`Shift copied to ${readableDate(form.date)}`, { description: 'The copy is an open shift — assign staff in the Coverage tab.' });
      (res.data?.no_agreement?.warnings || []).forEach((w) => toast.warning('Billed from the funding plan', { description: w.message, duration: 8000 }));
      (res.data?.warnings || []).forEach((w) => toast.warning(w.message, { duration: 8000 }));   // BS3-032: e.g. the copy has already ended
      onSaved?.(res.data?.data);
      onClose();
    } catch (e) {
      setError(duplicateErrorMessage(e));   // stays open so the chosen date is not lost
    } finally { setSaving(false); }
  };

  const types = [{ v: 'individual', label: '1:1 Support', icon: User }, { v: 'sil', label: 'SIL House', icon: Home }, { v: 'centre', label: 'Centre Session', icon: Users }];
  const activeType = sil ? 'sil' : 'individual';
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      {/* BR7 / TRI-CR-007: an outside click never closes the form */}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duplicate-shift-dialog"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if (saving) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="w-4 h-4" /> Duplicate Shift</DialogTitle>
          <DialogDescription>Copy this shift to another date. The copy is checked like a new shift.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="duplicate-shift-type">
          {types.map((t) => {
            const Icon = t.icon; const on = t.v === activeType;
            return (
              <div key={t.v} aria-disabled={!on} className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs ${on ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-slate-200 text-slate-400'}`}>
                <Icon className="w-4 h-4" /><span>{t.label}</span>
              </div>
            );
          })}
        </div>

        <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs space-y-0.5" data-testid="duplicate-shift-summary">
          <p><span className="text-muted-foreground">{sil ? 'House' : 'Participant'}:</span> <b>{sil ? (shift.house_name || 'SIL house') : (shift.participant_name || 'Participant')}</b>
            {sil && residents.length > 0 && <span className="text-muted-foreground"> · {residents.join(', ')}</span>}</p>
          <p><span className="text-muted-foreground">Original:</span> {readableDate(shift.date)} {shift.start_time}–{shift.end_time}</p>
          <p><span className="text-muted-foreground">Service:</span> {shift.service_name || shift.title || '—'}{items.length > 1 ? ` + ${items.length - 1} more service item${items.length > 2 ? 's' : ''}` : ''}
            {shift.support_ratio ? ` · ${shift.support_ratio}` : ''}</p>
          {shift.notes && <p className="truncate"><span className="text-muted-foreground">Notes:</span> {shift.notes}</p>}
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1 min-w-0"><Label className="text-xs">Copy to date</Label><TrinitiDatePicker value={form.date} onChange={(v) => set({ date: v })} testId="duplicate-shift-date" /></div>
            <div className="space-y-1 min-w-0"><Label className="text-xs">Start</Label><TrinitiTimePicker value={form.start_time} onChange={(v) => set({ start_time: v })} testId="duplicate-shift-start" /></div>
            <div className="space-y-1 min-w-0"><Label className="text-xs">End</Label><TrinitiTimePicker value={form.end_time} onChange={(v) => set({ end_time: v })} testId="duplicate-shift-end" /></div>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => set({ date: addDays(shift.date, 1) })} data-testid="duplicate-shift-next-day">Next day</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => set({ date: addDays(shift.date, 7) })} data-testid="duplicate-shift-next-week">Same day next week</Button>
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-slate-600" data-testid="duplicate-shift-staff-note">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Staff are not copied — the copy is an open shift. Attendance, timesheets and requests stay with the original.
          </p>
          {error && <p className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700" role="alert" data-testid="duplicate-shift-error">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="duplicate-shift-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="duplicate-shift-save">{saving ? 'Copying…' : 'Duplicate'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DuplicateShiftDialog;
